import React from 'react';
import { inputCls } from '../primitives/inputClasses';
import { usePreventWheelChange } from 'modules/Common/hooks/usePreventWheelChange';
import { normalizeDecimalInput } from 'helpers/numeric';
import { formatInputDisplay } from 'helpers/numberFormat';
import { controlAnchorProps } from 'modules/Common/utils/gapAnchor';

// text / email / number. `numeric` normalizes user input so European-style
// commas ("1,5") and US thousands separators ("1,234.56") both parse to a
// clean decimal string, renders the stored dot-normalized value back in the
// user's decimal convention (European users see "1,5"), and blocks the
// wheel from changing the value when the cursor is inside the input.
export default function TextField({
  field,
  fieldState,
  id,
  type = 'text',
  numeric = false,
  textarea = false,
  rows = 4,
  placeholder,
  disabled,
  // ADDITIVE (M5): undefined by default, so no existing consumer changes.
  // `readOnly` rather than `disabled` where a value is DERIVED rather than
  // forbidden — it stays selectable, copyable and focusable, and it still
  // submits, which a disabled input does not.
  readOnly,
  autoFocus,
  inputMode,
  onChangeOverride,
  // ADDITIVE: undefined by default. Caps input where the stored value has a schema limit.
  maxLength,
  list,
  anchor,
  empty,
  className = '',
}) {
  const handleWheel = usePreventWheelChange();

  const handleChange = (e) => {
    let v = e.target.value;
    if (numeric) v = normalizeDecimalInput(v);
    if (onChangeOverride) onChangeOverride(v, e);
    field.onChange(v);
  };

  const commonProps = {
    id,
    name: field.name,
    // Form state always holds the dot-normalized string; numeric fields
    // display it per the user's decimal convention (comma vs dot).
    value: numeric ? formatInputDisplay(field.value ?? '') : (field.value ?? ''),
    onChange: handleChange,
    onBlur: field.onBlur,
    placeholder,
    disabled,
    readOnly,
    autoFocus,
    inputMode,
    maxLength,
    ...controlAnchorProps(anchor, empty),
    className: inputCls(
      !!fieldState?.error,
      [readOnly ? 'cursor-default bg-slate-50 text-slate-600' : '', className]
        .filter(Boolean)
        .join(' '),
    ),
  };

  if (textarea) {
    return (
      <textarea
        rows={rows}
        {...commonProps}
      />
    );
  }

  return (
    <input
      type={numeric ? 'text' : type}
      list={list}
      {...commonProps}
      onWheel={numeric ? handleWheel : undefined}
    />
  );
}
